/**
 * Everything the document does not store but can be worked out from what it does.
 *
 * The model keeps each fact once. A bar that changes neither metre nor key says nothing
 * about either, a part's bars are keyed rather than ordered, and no event carries the time
 * at which it happens. That keeps edits small and local, and it means the questions an
 * editor actually asks -- what metre is in force here, is this bar full, where in the
 * piece is this note -- are answered by walking the document rather than by reading a
 * field. Those walks live here, in one place, so that no caller invents its own.
 *
 * Every function here is pure and takes the score it reads from. None of them memoise: the
 * documents are small enough that a scan costs less than a cache that can go stale, and a
 * stale answer about where a note is would be worse than a slow one.
 */

import type { EventId, MeasureId, PartId } from "./ids";
import { PPQ, measureTicks, ticksOf } from "./duration";
import type {
  Clef,
  GlobalMeasure,
  Instrument,
  KeySignature,
  MusicEvent,
  Part,
  PartMeasure,
  Score,
  Tempo,
  TimeSignature,
  Voice,
} from "./types";

/** What is in force at a bar, after walking back for whatever it does not state itself. */
export interface Attributes {
  readonly time: TimeSignature;
  readonly key: KeySignature;
  readonly tempo: Tempo | null;
}

/** Position of a bar in the piece, or `-1` when the score does not have it. */
export function measureIndexOf(score: Score, id: MeasureId): number {
  return score.measures.findIndex((measure) => measure.id === id);
}

/** The bar at an index, or `null` when the index is outside the piece. */
export function measureAt(score: Score, index: number): GlobalMeasure | null {
  return score.measures[index] ?? null;
}

/**
 * The metre, key and tempo in force at a bar.
 *
 * Each is the nearest statement at or before the bar. The first bar of a score always
 * states a metre and a key ({@link createScore} sees to it), so the walk always terminates;
 * the fallbacks below are for a document assembled some other way and are deliberately the
 * plainest possible rather than a guess.
 */
export function resolveAttributes(score: Score, index: number): Attributes {
  let time: TimeSignature | null = null;
  let key: KeySignature | null = null;
  let tempo: Tempo | null = null;

  for (let at = Math.min(index, score.measures.length - 1); at >= 0; at -= 1) {
    const measure = score.measures[at];
    if (measure === undefined) continue;
    time ??= measure.time;
    key ??= measure.key;
    tempo ??= measure.tempo;
    if (time !== null && key !== null && tempo !== null) break;
  }

  return {
    time: time ?? { beats: 4, beatType: 4, symbol: "common" },
    key: key ?? { fifths: 0, mode: "major" },
    tempo,
  };
}

/**
 * Ticks one bar of the piece holds.
 *
 * A bar states its own length only when it is short of its metre -- a pickup, or the bar
 * that closes a repeat begun mid-bar. Everything that asks how much fits in a bar asks
 * here, so the whole editor, the renderer and playback learn about a pickup at once.
 */
export function capacityOf(score: Score, index: number): number {
  const stated = score.measures[index]?.length ?? null;
  if (stated !== null) return stated;
  const { time } = resolveAttributes(score, index);
  return measureTicks(time.beats, time.beatType);
}

/** Ticks from the start of the piece to the start of a bar. */
export function measureOnset(score: Score, index: number): number {
  let onset = 0;
  for (let at = 0; at < index && at < score.measures.length; at += 1) {
    onset += capacityOf(score, at);
  }
  return onset;
}

/** A part's bar, or `null` when the part has no entry for it. */
export function partMeasureAt(part: Part, id: MeasureId): PartMeasure | null {
  return part.measures[id] ?? null;
}

/**
 * Ticks an event occupies in its bar, which is none at all for a grace note.
 *
 * A grace note is written with a note type -- it has to be, or it could not be drawn -- but
 * it takes its time from the note it leans on rather than from the bar. Counting its
 * written value would push everything after it later and make every bar containing one
 * look overfull.
 */
export function soundingTicks(event: MusicEvent): number {
  if (event.kind === "note" && event.grace !== null) return 0;
  return ticksOf(event.duration);
}

/**
 * Ticks an event occupies in a bar that holds `capacity`.
 *
 * The one event whose length is not its written duration is the whole-measure rest: it
 * spells itself `whole` whatever the metre, because it means "this bar is silent" rather
 * than "a semibreve happens here", and it fills whatever bar it is in.
 */
export function occupiedTicks(event: MusicEvent, capacity: number): number {
  if (event.kind === "rest" && event.wholeMeasure) return capacity;
  return soundingTicks(event);
}

/** Ticks a voice's events occupy. */
export function voiceTicks(voice: Voice): number {
  return voice.events.reduce((total, event) => total + soundingTicks(event), 0);
}

/** Ticks from the start of the bar to each event, in the voice's own order. */
export function eventOnsets(voice: Voice): readonly number[] {
  const onsets: number[] = [];
  let onset = 0;
  for (const event of voice.events) {
    onsets.push(onset);
    onset += soundingTicks(event);
  }
  return onsets;
}

/** How full one voice of one bar is. */
export interface Fill {
  readonly voice: Voice;
  readonly ticks: number;
  readonly capacity: number;
  readonly complete: boolean;
  readonly overfull: boolean;
}

/**
 * How full each voice of a part's bar is.
 *
 * A voice holding the bar's single whole-measure rest counts as exactly full whatever the
 * metre, because that rest means "this bar is silent" rather than "a semibreve happens
 * here" -- in 3/4 it still fills the bar, and measuring it as four quarters would report
 * every empty bar in triple time as overfull.
 */
export function fillOf(score: Score, part: Part, id: MeasureId): readonly Fill[] {
  const measure = partMeasureAt(part, id);
  if (measure === null) return [];
  const capacity = capacityOf(score, measureIndexOf(score, id));

  return measure.voices.map((voice) => {
    const silent = voice.events.length === 1 && isWholeMeasureRest(voice.events[0]);
    const ticks = silent ? capacity : voiceTicks(voice);
    return {
      voice,
      ticks,
      capacity,
      complete: ticks === capacity,
      overfull: ticks > capacity,
    };
  });
}

function isWholeMeasureRest(event: MusicEvent | undefined): boolean {
  return event !== undefined && event.kind === "rest" && event.wholeMeasure;
}

/** Where an event sits in the score. */
export interface EventLocation {
  readonly partId: PartId;
  readonly measureId: MeasureId;
  readonly measureIndex: number;
  readonly voiceIndex: number;
  readonly eventIndex: number;
  /** Ticks from the start of the piece. */
  readonly onset: number;
  readonly event: MusicEvent;
}

/**
 * Find an event by id, or `null` when no part holds it.
 *
 * This is the lookup that makes id addressing work: a selection, a spanner end, a clicked
 * SVG element and a remark from an outside analyser all name an event and nothing else,
 * and this turns that name back into a place.
 */
export function locateEvent(score: Score, id: EventId): EventLocation | null {
  for (const part of score.parts) {
    for (let index = 0; index < score.measures.length; index += 1) {
      const globalMeasure = score.measures[index];
      if (globalMeasure === undefined) continue;
      const measure = partMeasureAt(part, globalMeasure.id);
      if (measure === null) continue;

      for (let voiceIndex = 0; voiceIndex < measure.voices.length; voiceIndex += 1) {
        const voice = measure.voices[voiceIndex];
        if (voice === undefined) continue;
        const eventIndex = voice.events.findIndex((event) => event.id === id);
        if (eventIndex === -1) continue;
        const event = voice.events[eventIndex];
        if (event === undefined) continue;
        return {
          partId: part.id,
          measureId: globalMeasure.id,
          measureIndex: index,
          voiceIndex,
          eventIndex,
          onset: measureOnset(score, index) + (eventOnsets(voice)[eventIndex] ?? 0),
          event,
        };
      }
    }
  }
  return null;
}

/**
 * The clef on a staff at a bar: the nearest change at or before it, or the staff's own
 * opening clef when there has been none.
 */
export function clefAt(score: Score, part: Part, index: number, staff: number): Clef {
  for (let at = Math.min(index, score.measures.length - 1); at >= 0; at -= 1) {
    const globalMeasure = score.measures[at];
    if (globalMeasure === undefined) continue;
    const change = partMeasureAt(part, globalMeasure.id)?.clefChanges[staff];
    if (change !== undefined) return change;
  }
  return part.staves[staff]?.clef ?? { sign: "G", line: 2, octaveChange: 0 };
}

/**
 * What a part is playing at a bar: the instrument it took up most recently, or its own.
 *
 * The walk is the clef's, for the same reason: a doubling is stated where it happens and is
 * in force until something says otherwise, so the answer to "what is this player holding"
 * is the nearest statement at or before the bar. What a change cannot alter -- the kit, the
 * tuning, the way the part spells its signature -- comes from the part throughout, which is
 * why this answers with a whole {@link Instrument} and not with the change.
 *
 * Everything that reads a part's transposition at a bar asks here: the printed key, the
 * exported `<transpose>`, what a played key is spelled as, and what the notes sound.
 */
export function instrumentAt(score: Score, part: Part, index: number): Instrument {
  for (let at = Math.min(index, score.measures.length - 1); at >= 0; at -= 1) {
    const globalMeasure = score.measures[at];
    if (globalMeasure === undefined) continue;
    const change = partMeasureAt(part, globalMeasure.id)?.instrument ?? null;
    if (change !== null) return { ...part.instrument, ...change };
  }
  return part.instrument;
}

/** A part by id, or `null`. */
export function partById(score: Score, id: PartId): Part | null {
  return score.parts.find((part) => part.id === id) ?? null;
}

/** Ticks the whole piece lasts. */
export function scoreTicks(score: Score): number {
  return measureOnset(score, score.measures.length);
}

/** Seconds the whole piece lasts at its stated tempi, ignoring repeats. */
export function scoreSeconds(score: Score): number {
  let seconds = 0;
  for (let index = 0; index < score.measures.length; index += 1) {
    const { tempo } = resolveAttributes(score, index);
    const perQuarter = tempo === null ? 0.5 : 60 / (tempo.bpm * (ticksOf(tempo.unit) / PPQ));
    seconds += (capacityOf(score, index) / PPQ) * perQuarter;
  }
  return seconds;
}
